// Backend #623: one recorded resolution observation — the input snapshot plus full
// per-resolver provenance the chain wrote when it resolved (or failed to resolve) a product's
// category / VAT rate. The store is append-only and high-volume by design: the endpoints are
// newest-first with a server-side take cap (max 500), a view pages and never tries to be
// exhaustive. Per-shop reading is correlationKeyPrefix = '<bindingId>:'.

export interface ResolutionObservationStepBase {
  pass: number;
  stepOrder: number;
  resolverKey: string;
  tier: number;
  kind: string;
  rawConfidence?: number;
  // Human-readable why (e.g. the keyword tier's '"Milch" -> 7 % (…)') — display/diagnosis
  // only, never parsed for logic beyond the leading quoted term offered as a prefill chip.
  evidence?: string;
  runnerUpConfidence?: number;
  elapsedMs: number;
}

export interface CategoryResolutionObservationStep extends ResolutionObservationStepBase {
  candidateProductCategoryId?: string;
  runnerUpProductCategoryId?: string;
}

export interface VatResolutionObservationStep extends ResolutionObservationStepBase {
  candidateVatId?: string;
  runnerUpVatId?: string;
}

export interface ResolutionObservationBase {
  id: string;
  // '<bindingId>:<externalId>' from the scan pipeline; other writers use their own prefixes.
  correlationKey: string;
  profileKey: string;
  // 'Observe' | 'Live'
  mode: string;
  // The content language the resolution ran under ('de'/'en'), or null = undeclared
  // (every vocabulary group in scope, homograph ties escalate as Ambiguous).
  contentLanguage?: string;
  name?: string;
  gtin?: string;
  // Raw source category strings as the shop delivered them (breadcrumbs like
  // 'Bücher > Romane' — the resolvers match per segment).
  sourceCategories: string[];
  winningResolverKey?: string;
  confidence?: number;
  // 'Resolved' | 'Ambiguous' | 'Exhausted'
  outcome: string;
  createdAtUtc: string;
}

export interface CategoryResolutionObservation extends ResolutionObservationBase {
  appliedProductCategoryId?: string;
  resolvedProductCategoryId?: string;
  // Backend #633 B: the FULL set verdict, primary first (empty when nothing resolved) — a
  // set verdict has more than one entry; the single field above stays the primary.
  resolvedProductCategoryIds?: string[];
  steps: CategoryResolutionObservationStep[];
}

export interface VatResolutionObservation extends ResolutionObservationBase {
  appliedVatId?: string;
  resolvedVatId?: string;
  steps: VatResolutionObservationStep[];
}

export const ResolutionOutcomeConstants = {
  Resolved: 'Resolved',
  Ambiguous: 'Ambiguous',
  Exhausted: 'Exhausted',
} as const;
